import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";

/**
 * Manually-entered licenses (any vendor — Cisco non-Meraki, Fortinet,
 * Sophos, Microsoft, etc.). Lives next to the Meraki Licenses card on
 * the customer detail page so techs see "everything renewing" in one
 * place. The unified renewal sweep (daily 6:05 AM ET) hits both this
 * table and the Meraki one — opens a ticket + calendar entry 30 days
 * before each expiration regardless of source.
 */

type Manual = {
  id: number;
  customer_id: number;
  vendor: string;
  product: string;
  reference: string | null;
  support_contact: string | null;
  support_phone: string | null;
  seat_count: number | null;
  annual_cost_cents: number | null;
  expiration_date: string;
  days_remaining: number | null;
  severity: "ok" | "warning" | "critical" | "expired" | null;
  notes: string | null;
  alert_30d_sent_at: string | null;
  renewal_ticket_id: number | null;
  calendar_event_id: number | null;
  created_at: string | null;
};

const SEV_PILL: Record<string, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  expired:
    "bg-red-200 text-red-900 font-bold dark:bg-red-900/50 dark:text-red-200",
};

const SUGGESTED_VENDORS = [
  "Cisco",
  "Fortinet",
  "Sophos",
  "Microsoft",
  "VMware",
  "Veeam",
  "WatchGuard",
  "Palo Alto",
  "Acronis",
  "DNSFilter",
  "Huntress",
];

function blankDraft(): Partial<Manual> {
  return {
    vendor: "Cisco",
    product: "",
    reference: "",
    seat_count: undefined,
    annual_cost_cents: undefined,
    expiration_date: "",
    notes: "",
  };
}

export default function ManualLicensesCard({
  customerId,
  isAdmin,
}: {
  customerId: number;
  isAdmin: boolean;
}) {
  const [licenses, setLicenses] = useState<Manual[]>([]);
  const [editing, setEditing] = useState<Partial<Manual> | null>(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const r = await api.get(
        `/api/v1/customers/${customerId}/manual-licenses`,
      );
      setLicenses(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load licenses");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setErr("");
    try {
      if (editing.id) {
        await api.patch(`/api/v1/manual-licenses/${editing.id}`, editing);
      } else {
        await api.post(
          `/api/v1/customers/${customerId}/manual-licenses`,
          editing,
        );
      }
      setEditing(null);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lic: Manual) => {
    if (!window.confirm(`Delete ${lic.vendor} / ${lic.product}?`)) return;
    try {
      await api.delete(`/api/v1/manual-licenses/${lic.id}`);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Other licenses (manually tracked)
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            For vendors without an auto-pull API — Cisco non-Meraki, Fortinet,
            Sophos, Microsoft licensing, etc. Each entry feeds the same
            renewal pipeline as Meraki: 30 days before expiry the system
            auto-opens a ticket on the customer's queue and emails the support
            inbox.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setEditing(blankDraft())}
            className="text-xs px-3 py-1.5 bg-brand text-white rounded-lg font-medium hover:opacity-90"
          >
            + Add license
          </button>
        )}
      </div>

      {err && (
        <div className="text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {err}
        </div>
      )}

      {licenses.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          No manual licenses tracked. Click <strong>Add license</strong> to
          record a renewable contract (Cisco support, Microsoft seats,
          Fortinet support, etc.).
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Vendor</th>
              <th className="text-left px-3 py-2 font-medium">Product</th>
              <th className="text-left px-3 py-2 font-medium">Reference</th>
              <th className="text-left px-3 py-2 font-medium">Expires</th>
              <th className="text-left px-3 py-2 font-medium">Days</th>
              <th className="text-left px-3 py-2 font-medium">Ticket</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {licenses.map((lic) => {
              const pill = SEV_PILL[lic.severity || "ok"] || SEV_PILL.ok;
              return (
                <tr key={lic.id} className="text-gray-900 dark:text-gray-100">
                  <td className="px-3 py-2 font-medium">{lic.vendor}</td>
                  <td className="px-3 py-2 text-xs">
                    <div>{lic.product}</div>
                    {lic.seat_count != null && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {lic.seat_count} seats
                      </div>
                    )}
                    {(lic.support_contact || lic.support_phone) && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        Support: {lic.support_contact || "—"}
                        {lic.support_phone ? ` · ${lic.support_phone}` : ""}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono">
                    {lic.reference || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs">{lic.expiration_date}</td>
                  <td className="px-3 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${pill}`}>
                      {lic.severity === "expired"
                        ? `expired ${Math.abs(lic.days_remaining || 0)}d ago`
                        : `${lic.days_remaining ?? "?"} days`}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {lic.renewal_ticket_id ? (
                      <Link
                        to={`/tickets/${lic.renewal_ticket_id}`}
                        className="text-brand dark:text-brand-light hover:underline"
                      >
                        #{lic.renewal_ticket_id}
                      </Link>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => setEditing(lic)}
                          className="text-xs text-brand dark:text-brand-light hover:underline mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => remove(lic)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              {editing.id ? "Edit license" : "Add license"}
            </h3>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Vendor</span>
                <input
                  list="vendor-suggestions"
                  value={editing.vendor ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, vendor: e.target.value })
                  }
                  className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                />
                <datalist id="vendor-suggestions">
                  {SUGGESTED_VENDORS.map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Product / SKU
                </span>
                <input
                  value={editing.product ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, product: e.target.value })
                  }
                  placeholder="e.g. Catalyst 9300 SmartLicense Essentials"
                  className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Reference / contract ID (optional)
                </span>
                <input
                  value={editing.reference ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, reference: e.target.value })
                  }
                  placeholder="SO-12345, smart-license token, etc."
                  className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Support contact (optional)
                  </span>
                  <input
                    value={editing.support_contact ?? ""}
                    onChange={(e) =>
                      setEditing({ ...editing, support_contact: e.target.value })
                    }
                    placeholder="name / email / portal"
                    className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Support phone (optional)
                  </span>
                  <input
                    value={editing.support_phone ?? ""}
                    onChange={(e) =>
                      setEditing({ ...editing, support_phone: e.target.value })
                    }
                    className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Seat count (optional)
                  </span>
                  <input
                    type="number"
                    value={editing.seat_count ?? ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        seat_count: e.target.value
                          ? Number(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                    Annual cost $ (optional)
                  </span>
                  <input
                    type="number"
                    value={
                      editing.annual_cost_cents != null
                        ? editing.annual_cost_cents / 100
                        : ""
                    }
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        annual_cost_cents: e.target.value
                          ? Math.round(Number(e.target.value) * 100)
                          : undefined,
                      })
                    }
                    className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Expiration date
                </span>
                <input
                  type="date"
                  value={editing.expiration_date ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      expiration_date: e.target.value,
                    })
                  }
                  className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                />
              </label>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Notes</span>
                <textarea
                  value={editing.notes ?? ""}
                  onChange={(e) =>
                    setEditing({ ...editing, notes: e.target.value })
                  }
                  rows={3}
                  className="w-full border dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900"
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 text-sm border dark:border-gray-600 dark:text-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={
                  saving ||
                  !editing.vendor ||
                  !editing.product ||
                  !editing.expiration_date
                }
                className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
